<template>
  <div class="block block-mode-loading-oneui">
    <div class="block-header border-bottom">
      <h3 class="block-title">
        Latest Client Enquiries
      </h3>
    </div>
    <div class="block-content block-content-full">
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <div
          class="spinner-grow text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>

      <div v-else>
        <div class="table-responsive">
          <table
            v-if="contacts.length"
            class="table table-bordered table-striped table-vcenter"
          >
            <thead class="table-dark">
              <tr>
                <th
                  class="text-center"
                  style="width: 80px;"
                >
                  No.
                </th>
                <th style="width: 140px;">
                  Submitted At
                </th>
                <th>Contact Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Enquiry</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(contact, index) in contacts"
                :key="contact.id"
              >
                <td class="text-center">
                  {{ (pagination.from || 0) + index }}
                </td>
                <td>{{ formatDate(contact.created_at) }}</td>
                <td>{{ contact.contact_name }}</td>
                <td>{{ contact.email }}</td>
                <td>{{ contact.phone_number || '-' }}</td>
                <td>{{ contact.company || '-' }}</td>
                <td>{{ truncateEnquiry(contact.enquiry) }}</td>
              </tr>
            </tbody>
          </table>
          <span v-else>No client enquiries found.</span>
        </div>

        <nav
          v-if="pagination.last_page > 1"
          aria-label="Latest Client Enquiries Pagination"
          class="mt-3"
        >
          <ul class="pagination justify-content-center">
            <li
              class="page-item"
              :class="{ disabled: parseInt(pagination.current_page) === 1 }"
            >
              <a
                class="page-link"
                href="#"
                :tabindex="parseInt(pagination.current_page) === 1 ? -1 : 0"
                @click.prevent="changePage(pagination.current_page - 1)"
              >
                <i class="fa fa-angle-left" />
              </a>
            </li>

            <li
              v-for="page in visiblePages"
              :key="page"
              class="page-item"
              :class="{ active: parseInt(page) === parseInt(pagination.current_page) }"
            >
              <a
                class="page-link"
                href="#"
                @click.prevent="changePage(page)"
              >
                {{ page }}
              </a>
            </li>

            <li
              class="page-item"
              :class="{ disabled: parseInt(pagination.current_page) === parseInt(pagination.last_page) }"
            >
              <a
                class="page-link"
                href="#"
                :tabindex="parseInt(pagination.current_page) === parseInt(pagination.last_page) ? -1 : 0"
                @click.prevent="changePage(pagination.current_page + 1)"
              >
                <i class="fa fa-angle-right" />
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'

export default {
  name: 'LatestClientEnquiries',

  data() {
    return {
      maxVisiblePages: 5
    }
  },

  computed: {
    ...mapGetters('dashboard', [
      'latestClientContacts',
      'latestClientContactsPagination',
      'latestClientContactsLoading',
      'latestClientContactsError'
    ]),

    contacts() {
      return this.latestClientContacts
    },

    pagination() {
      return this.latestClientContactsPagination
    },

    loading() {
      return this.latestClientContactsLoading
    },

    error() {
      return this.latestClientContactsError
    },

    visiblePages() {
      const current = this.pagination.current_page
      const last = this.pagination.last_page
      const maxVisible = this.maxVisiblePages

      if (last <= maxVisible) {
        return Array.from({ length: last }, (_, i) => i + 1)
      }

      const half = Math.floor(maxVisible / 2)
      let start = Math.max(1, current - half)
      let end = Math.min(last, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    ...mapActions('dashboard', ['fetchLatestClientContacts']),

    async fetchData() {
      try {
        await this.fetchLatestClientContacts()
      } catch (error) {
        console.error('Failed to fetch latest client contacts:', error)
      }
    },

    async changePage(page) {
      const targetPage = parseInt(page)
      const currentPage = parseInt(this.pagination.current_page)
      const lastPage = parseInt(this.pagination.last_page)

      if (targetPage < 1 || targetPage > lastPage || targetPage === currentPage) {
        return
      }

      try {
        await this.fetchLatestClientContacts({ page: targetPage })
      } catch (error) {
        console.error('Failed to change page:', error)
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('YYYY-MM-DD HH:mm')
    },

    truncateEnquiry(enquiry) {
      if (!enquiry) {
        return ''
      }

      return enquiry.length > 80 ? `${enquiry.slice(0, 80)}...` : enquiry
    }
  }
}
</script>

<style scoped>
.page-item.disabled .page-link {
  pointer-events: none;
  cursor: default;
}
</style>
